"use client";

import {
  ArrowClockwise,
  House,
  Phone,
  Warning,
} from "@phosphor-icons/react";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/shared/libs/commonUtils";

const CHUNK_RELOAD_STORAGE_PREFIX = "mdsafar.chunk-reload-attempted";

interface AppErrorBoundaryProps {
  error?: Error & { digest?: string };
  reset?: () => void;
}

export default function AppErrorBoundary({
  error,
  reset,
}: AppErrorBoundaryProps) {
  const reportedRef = useRef(false);
  const [fallbackErrorCode] = useState(() =>
    Math.random().toString(36).slice(2, 8).toUpperCase(),
  );
  const [isReloading, setIsReloading] = useState(false);
  const errorCode = `ERR_${(error?.digest ?? fallbackErrorCode)
    .toString()
    .slice(0, 8)
    .toUpperCase()}`;

  useEffect(() => {
    if (!isChunkLoadError(error)) return;

    const reloadKey = `${CHUNK_RELOAD_STORAGE_PREFIX}:${window.location.pathname}`;
    if (window.sessionStorage.getItem(reloadKey)) return;

    window.sessionStorage.setItem(reloadKey, "true");
    setIsReloading(true);
    window.location.reload();
  }, [error]);

  useEffect(() => {
    if (reportedRef.current) return;

    reportedRef.current = true;
    reportApplicationError(error, errorCode);
  }, [error, errorCode]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-muted/40 px-4 py-10"
      dir="rtl"
    >
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-background shadow-xl">
        <div className="h-1.5 w-full bg-linear-to-l from-[#e85d2f] to-[#0a3d6b]" />

        <div className="flex flex-col items-center px-8 py-10 text-center">
          <Image
            src="/images/logo-persian.webp"
            alt="لوگو"
            width={180}
            height={48}
            className="mb-8 h-8 w-auto object-contain opacity-75"
            priority
          />

          <div className="relative mb-6">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-destructive/10 shadow-md">
              <Warning size={36} className="text-destructive" weight="duotone" />
            </div>
            <div className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full border-2 border-background bg-destructive">
              <span className="text-[0.6rem] font-bold text-background">
                !
              </span>
            </div>
          </div>

          <h1 className="mb-2 text-xl font-bold text-foreground">
            مشکلی پیش آمد
          </h1>
          <p className="mb-1 text-sm leading-7 text-muted-foreground">
            متأسفانه بخشی از برنامه با خطا مواجه شد.
          </p>
          <p className="mb-7 text-sm leading-7 text-muted-foreground">
            این مشکل گزارش شده و در حال بررسی است.
          </p>

          <div className="w-full space-y-3">
            {reset ? (
              <button
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0a3d6b] py-3 text-sm font-semibold text-white transition hover:opacity-90 active:scale-[0.99] disabled:cursor-wait disabled:opacity-80"
                disabled={isReloading}
                onClick={() => {
                  setIsReloading(true);
                  window.location.reload();
                }}
                type="button"
              >
                <ArrowClockwise
                  size={16}
                  className={cn(isReloading && "animate-spin")}
                />
                {isReloading ? "در حال بارگذاری" : "بارگذاری مجدد صفحه"}
              </button>
            ) : null}
            <button
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-border py-3 text-sm font-medium text-foreground transition hover:bg-secondary active:scale-[0.99]"
              onClick={() => {
                window.location.href = "/";
              }}
              type="button"
            >
              <House size={16} />
              بازگشت به صفحه اصلی
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border bg-muted px-8 py-4">
          <p className="text-xs text-muted-foreground">
            اگر مشکل ادامه داشت با پشتیبانی تماس بگیرید
          </p>
          <a
            className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-primary hover:underline"
            href="tel:02191094028"
          >
            <Phone size={11} />
            ۰۲۱۹۱۰۹۴۰۲۸
          </a>
        </div>
      </div>

      <p className="mt-6 text-xs text-muted-foreground">کد خطا: {errorCode}</p>
    </div>
  );
}

function isChunkLoadError(error?: Error) {
  const name = error?.name ?? "";
  const message = error?.message ?? "";

  return (
    name === "ChunkLoadError" ||
    message.includes("ChunkLoadError") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk")
  );
}

function reportApplicationError(
  error: (Error & { digest?: string }) | undefined,
  errorCode: string,
) {
  const now = new Date();
  const locationHref = window.location.href;
  const userId = readCookie("user-id");
  const device = readCookie("device");

  void fetch("/api/log/backend-error/", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      id: errorCode.replace("ERR_", "FE-"),
      time: now.toISOString(),
      service: "frontend",
      method: "CLIENT",
      url: locationHref,
      status: 0,
      statusText: "Application Error",
      durationMs: 0,
      path: window.location.pathname,
      message: "Application error boundary rendered",
      request: {
        headers: {
          "X-Agent": navigator.userAgent,
          "X-User-ID": userId,
          "X-Device": device,
          "X-Current-Path": window.location.pathname,
          "X-Current-Search": window.location.search,
          "X-Occurred-At": now.toISOString(),
        },
      },
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        cause: "cause" in (error ?? {}) ? error?.cause : undefined,
        digest: error?.digest,
      },
    }),
  }).catch(() => undefined);
}

function readCookie(name: string) {
  return (
    document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(`${name}=`))
      ?.split("=")
      .slice(1)
      .join("=") || ""
  );
}
